import { format } from "date-fns";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { DateRange } from "react-day-picker";
import { IRangePicker, RangePickerHandle } from "../Datepicker.types";
import Picker from "../Picker";
import RangeWrapper from "./RangeWrapper";

const RangePicker = forwardRef<RangePickerHandle, IRangePicker>(({
  range: _range,
  onApply,
  onChange,
  paddingType,
  pickerProps,
  onlyModal = false,
  showApply,
  disabled,
  hasHelpers,
  ...labelProps
}, ref) => {
  const [range, setRange] = useState<DateRange | undefined>(_range);
  const [show, setShow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Imperative open(): pop the calendar open and move focus into it so the user
  // can pick a date right away (no extra click). Focuses the first focusable
  // element inside the picker container (the "from" date input on desktop, or
  // the modal on touch); falls back to the container itself.
  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        if (disabled) return;
        setShow(true);
        requestAnimationFrame(() => {
          const root = containerRef.current;
          if (!root) return;
          const focusable = root.querySelector<HTMLElement>(
            "input, button, [tabindex]:not([tabindex='-1'])"
          );
          (focusable || root).focus();
        });
      }
    }),
    [disabled]
  );
  const [inputRange, setInputRange] = useState<{ from: string; to: string }>(
    range
      ? {
        from: range?.from ? format(range?.from, "dd/MM/yyyy") : "",
        to: range?.to ? format(range?.to, "dd/MM/yyyy") : ""
      }
      : {
        from: "",
        to: ""
      }
  );

  useEffect(() => {
    setRange(_range);
    setInputRange(() => ({
      from: _range?.from ? format(_range?.from, "dd/MM/yyyy") : "",
      to: _range?.to ? format(_range?.to, "dd/MM/yyyy") : ""
    }));
  }, [_range]);

  const onButtonApply = () => {
    onApply && onApply(range);
    setShow(false);
  };

  const _onChangeRange = (r: DateRange | undefined) => {
    onChange && onChange(r);
    setRange(r);
  };

  return (
    <div ref={containerRef} className="w-full">
    <RangeWrapper
      show={show}
      setShow={setShow}
      wrapperClassName="relative w-full"
      inputRange={inputRange}
      setInputRange={setInputRange}
      onChangeRange={_onChangeRange}
      range={range}
      paddingType={paddingType}
      onlyModal={onlyModal}
      disabled={disabled}
      {...labelProps}
    >
      <div
        className=""
      >
        <Picker
          {...pickerProps}
          disabled={disabled}
          mode="range"
          defaultMonth={range?.from}
          selected={range}
          hasHelpers={hasHelpers}
          onSelect={(r: DateRange | undefined) => {
            setRange(r);
            onChange && onChange(r);
            setInputRange(() => ({
              from: r?.from ? format(r?.from, "dd/MM/yyyy") : "",
              to: r?.to ? format(r?.to, "dd/MM/yyyy") : ""
            }));
          }}
          onCancel={() => setShow(false)}
          onApply={onButtonApply}
          showApply={showApply}
        />
      </div>
    </RangeWrapper>
    </div>
  );
});

RangePicker.displayName = "RangePicker";

export default RangePicker;
